import { useCreateInviteForm } from '@frontend/forms';
import { useEffect } from '@frontend/react';
import type { CommonModalProps } from '@frontend/web-react';
import { Button, Divider, Icon, Modal, PhoneNumberInput, Text, TextInput, XStack, YStack } from '@frontend/web-react';
import { useAnalytics, useCreateInviteMutation } from '@frontend/web/hooks';
import { handleMutation } from '@frontend/web/utils';

export type InviteHouseholdMemberModalProps = CommonModalProps & {
  householdId: string;
  showBackButton?: boolean;
};

export const InviteHouseholdMemberModal = ({
  householdId,
  isOpen,
  onClose,
  showBackButton = false,
}: InviteHouseholdMemberModalProps) => {
  const { track } = useAnalytics();
  const [createInvite, { isLoading }] = useCreateInviteMutation();

  useEffect(() => {
    if (isOpen) {
      track({ event: 'inviteModalOpened', topic: 'invite' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const { dirty, getControl, handleSubmit, isValid, resetForm, values } = useCreateInviteForm({
    onSubmit: (values) => {
      handleMutation({
        mutation: () => createInvite({ ...values, id: householdId }).unwrap(),
        onSuccess: () => {
          resetForm();
          onClose();
        },
        successMessage: `${values.firstName} has been invited`,
      });
    },
    validateOnMount: false,
  });

  return (
    <Modal
      Header={
        showBackButton ? (
          <YStack>
            <XStack alignItems="center" justifyContent="between" p="md">
              <Icon canFocus enableBackgroundColor={false} name="IconChevronLeft" onClick={onClose} />
              <Text color="secondary" type="h5">
                Add a member
              </Text>
            </XStack>
            <Divider />
          </YStack>
        ) : undefined
      }
      isOpen={isOpen}
      onClose={onClose}
      title={showBackButton ? undefined : 'Add a member'}
      titlesAlignment="start"
    >
      <form onSubmit={handleSubmit}>
        <YStack className="gap-md">
          <XStack className="gap-md">
            <TextInput {...getControl('firstName')} label="First name" />
            <TextInput {...getControl('lastName')} label="Last name" />
          </XStack>
          <Text color="secondary" type="caption">
            At least one contact method is required
          </Text>
          <PhoneNumberInput
            {...getControl('phoneNumber')}
            label="Phone number"
            showOptionalText
            value={values.phoneNumber ?? ''}
          />
          <TextInput {...getControl('email')} label="Email" showOptionalText value={values.email as string} />
          <Button
            className="mt-md"
            disabled={!isValid || !dirty}
            fullWidth
            isForm
            isLoading={isLoading}
            label="Send invite"
          />
        </YStack>
      </form>
    </Modal>
  );
};
